import { proxy } from "valtio";

import { normalizeInviteCode } from "./inviteCode";

/**
 * 邀请码内存 store —— 供 UI 响应式读取（注册页的 applied 态、填写页的预填值）。
 *
 * 与 cookie（{@link import("./inviteCode").INVITE_CODE_COOKIE_KEY}）并行存在：
 * - cookie 负责**跨会话存活**（点链接与完成注册常不在同一次会话）
 * - store 负责**当前页面响应式渲染**
 * 写入点两者同步（见 `inviteCodeCookie.client.ts`）。
 */
const inviteCodeStore = proxy<{
  /** 当前生效的邀请码；null 表示未获取到。 */
  code: string | null;
  /** 来源：`link` = 从邀请链接/深链自动捕获；`manual` = 用户在填写页手动输入。 */
  source: "link" | "manual" | null;
  /**
   * 后端是否**确认识别**了这个码（`GET /auth/validate`）。
   *
   * - `true`  → 展示 `✓ Referral code XXXX applied`
   * - `false` → 展示 `ⓘ Referral code XXXX not applied`（**统一标识**：后端说无效、429 限流、
   *   网络失败三种情况共用此态 —— 用户只需知道「码传进来了但没生效」，不必区分原因）
   * - `null`  → 尚未校验完，乐观按已识别展示（多数码是有效的，避免先闪一下"未生效"）
   *
   * 注意它**只影响展示**：无论取值如何，`OS-Invite-Code` 照发，后端注册时会再判一次。
   */
  applied: boolean | null;
}>({
  code: null,
  source: null,
  applied: null,
});

/** 只读快照供非 React 上下文（请求头合并等）读取。 */
export function getInviteCodeFromStore(): string | null {
  return inviteCodeStore.code;
}

/** 来源标记，供埋点与「是否展示 applied 态」判断。 */
export function getInviteCodeSourceFromStore(): "link" | "manual" | null {
  return inviteCodeStore.source;
}

/** 记录后端校验结论（只影响展示，不影响是否发送请求头）。 */
export function setInviteCodeAppliedInStore(applied: boolean | null): void {
  inviteCodeStore.applied = applied;
}

/**
 * 写入邀请码（**不改大小写**，后端精确匹配）。空值等价于清除。
 *
 * **只有码真的变了才作废校验结论**。这不是优化，是正确性：
 * `restoreInviteCodeFromCookie()` 会在**每次路由变化**时用同一个码重新写入
 * （见 `useInviteCodeCapture`），若无条件把 `applied` 归零，就会出现
 *
 * ```
 * /task/invite/BADCODE  → 校验 invalid → applied=false（not applied ✅）
 *   ↓ 跳 /login
 * restore 同一个码      → applied 被重置为 null
 *                       → validatedInviteCodeRef 挡掉重新校验
 *                       → 停在 null，而 InviteCodeEntryRow 对 null 按乐观态渲染
 *                       → 无效码显示成「applied」❌
 * ```
 */
export function setInviteCodeInStore(
  value: string | null | undefined,
  source: "link" | "manual",
): void {
  const normalized = normalizeInviteCode(value);
  const codeChanged = normalized !== inviteCodeStore.code;
  inviteCodeStore.code = normalized;
  inviteCodeStore.source = normalized ? source : null;
  if (codeChanged) {
    // 换了码 → 旧结论不再适用，回到「尚未校验」的乐观态等新结论
    inviteCodeStore.applied = null;
  }
}

/** 清除（用户在填写页清空、或注册完成后收尾）。 */
export function clearInviteCodeInStore(): void {
  inviteCodeStore.code = null;
  inviteCodeStore.source = null;
  inviteCodeStore.applied = null;
}

/** React 组件用 `useSnapshot(inviteCodeState)` 订阅。 */
export const inviteCodeState = inviteCodeStore;
